import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BriefcaseBusiness, FileText, Upload, Check, X, Trash2, Download, UserCircle } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const DOC_TYPES = ["Payslip", "Offer Letter", "ID Card", "Joining Letter", "Appointment Letter", "Form 16", "Form 26AS", "PAN", "Aadhar", "CV", "Other"];
const statusBadge = {
  Pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800",
  Approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800",
  Rejected: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800",
};

export default function HRAdmin() {
  const [tab, setTab] = useState("approvals");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selEmp, setSelEmp] = useState(null);
  const [empDocs, setEmpDocs] = useState([]);
  const [docType, setDocType] = useState("Payslip");
  const [docLabel, setDocLabel] = useState("");
  const [rejectFor, setRejectFor] = useState(null);
  const [comment, setComment] = useState("");
  const fileInput = useRef(null);

  const loadRequests = () => api.get(`/hr/requests${statusFilter ? `?status=${statusFilter}` : ""}`).then((r) => setRequests(r.data)).catch(() => {});
  const loadEmployees = () => api.get("/hr/employees").then((r) => setEmployees(r.data)).catch(() => {});
  const loadEmpDocs = (id) => api.get(`/hr/employees/${id}/documents`).then((r) => setEmpDocs(r.data)).catch(() => {});
  useEffect(() => { loadRequests(); }, [statusFilter]);
  useEffect(() => { loadEmployees(); }, []);
  useEffect(() => { if (selEmp) loadEmpDocs(selEmp.id); }, [selEmp]);

  const decide = async (req, status, note = "") => {
    try {
      await api.put(`/hr/requests/${req.id}/decision`, { status, comment: note });
      toast.success(`${req.kind} request ${status.toLowerCase()}`);
      loadRequests();
    } catch (e) { toast.error("Could not update request"); }
  };

  const submitReject = async () => {
    if (!rejectFor) return;
    await decide(rejectFor, "Rejected", comment);
    setRejectFor(null); setComment("");
  };

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !selEmp) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/hr/employees/${selEmp.id}/documents?doc_type=${encodeURIComponent(docType)}&label=${encodeURIComponent(docLabel)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setDocLabel(""); loadEmpDocs(selEmp.id);
    } catch (err) { toast.error("Could not upload document"); }
    e.target.value = "";
  };

  const deleteDoc = async (id) => {
    try { await api.delete(`/hr/documents/${id}`); loadEmpDocs(selEmp.id); toast.success("Deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6">
      <div><h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2"><BriefcaseBusiness size={24} className="text-primary" /> HR Admin</h1><p className="text-muted-foreground mt-1">Approve leave, permission & break requests and manage employee documents.</p></div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-sm">
          <TabsTrigger value="approvals" className="rounded-sm gap-1.5" data-testid="tab-approvals"><Check size={14} /> Approvals</TabsTrigger>
          <TabsTrigger value="employees" className="rounded-sm gap-1.5" data-testid="tab-employees"><UserCircle size={14} /> Employees & Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="approvals" className="mt-4 space-y-4 animate-in fade-in duration-300">
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList className="rounded-sm">
              <TabsTrigger value="Pending" className="rounded-sm" data-testid="filter-pending">Pending</TabsTrigger>
              <TabsTrigger value="Approved" className="rounded-sm" data-testid="filter-approved">Approved</TabsTrigger>
              <TabsTrigger value="Rejected" className="rounded-sm" data-testid="filter-rejected">Rejected</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="rounded-sm border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>When</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody data-testid="hr-requests-table">
                {requests.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-10">No {statusFilter.toLowerCase()} requests.</TableCell></TableRow>}
                {requests.map((r) => (
                  <TableRow key={r.id} data-testid={`hr-request-${r.id}`}>
                    <TableCell className="font-medium">{r.employee_name}</TableCell>
                    <TableCell>{r.kind}</TableCell>
                    <TableCell className="text-sm font-mono">{r.kind === "Leave" ? `${r.start_date} → ${r.end_date}` : `${r.day} ${r.from_time || ""}-${r.to_time || ""}`}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{r.reason || "—"}</TableCell>
                    <TableCell><Badge variant="outline" className={`rounded-sm ${statusBadge[r.status]}`}>{r.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      {r.status === "Pending" ? (
                        <div className="flex items-center gap-2 justify-end">
                          <Button size="sm" className="rounded-sm gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => decide(r, "Approved")} data-testid={`approve-${r.id}`}><Check size={14} /> Approve</Button>
                          <Button size="sm" variant="outline" className="rounded-sm gap-1 text-red-600 border-red-300 hover:bg-red-50 dark:hover:bg-red-950/20" onClick={() => { setRejectFor(r); setComment(""); }} data-testid={`reject-${r.id}`}><X size={14} /> Reject</Button>
                        </div>
                      ) : <span className="text-xs text-muted-foreground">by {r.decided_by || "—"}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="employees" className="mt-4 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="col-span-12 lg:col-span-4 rounded-sm border border-border bg-card overflow-hidden">
              <div className="px-4 py-3 border-b border-border"><h3 className="font-heading font-semibold text-sm">Employees ({employees.length})</h3></div>
              <div className="divide-y divide-border max-h-[520px] overflow-y-auto" data-testid="hr-employees-list">
                {employees.map((e) => (
                  <button key={e.id} onClick={() => setSelEmp(e)} data-testid={`emp-${e.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors ${selEmp?.id === e.id ? "bg-primary/5" : ""}`}>
                    {e.photo_path ? <img src={fileUrl(e.photo_path)} alt={e.name} className="h-8 w-8 rounded-full object-cover border border-border" /> : <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center"><UserCircle size={18} /></div>}
                    <div className="min-w-0"><p className="text-sm font-medium truncate">{e.name}</p><p className="text-xs text-muted-foreground capitalize truncate">{e.role}</p></div>
                  </button>
                ))}
              </div>
            </div>

            <div className="col-span-12 lg:col-span-8 space-y-4">
              {!selEmp ? (
                <div className="rounded-sm border border-border bg-card p-10 text-center text-sm text-muted-foreground">Select an employee to manage their documents.</div>
              ) : (
                <>
                  <div className="rounded-sm border border-border bg-card p-6 space-y-4">
                    <h3 className="font-heading font-semibold flex items-center gap-2"><Upload size={16} className="text-primary" /> Upload document for {selEmp.name}</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label>Type</Label>
                        <Select value={docType} onValueChange={setDocType}>
                          <SelectTrigger className="rounded-sm" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-1.5"><Label>Label (optional)</Label><Input className="rounded-sm" value={docLabel} onChange={(e) => setDocLabel(e.target.value)} placeholder="e.g. Payslip – June 2026" data-testid="doc-label-input" /></div>
                    </div>
                    <input ref={fileInput} type="file" className="hidden" onChange={uploadDoc} data-testid="doc-file-input" />
                    <Button className="rounded-sm gap-2" onClick={() => fileInput.current?.click()} data-testid="upload-doc-btn"><Upload size={15} /> Choose file & upload</Button>
                  </div>

                  <div className="rounded-sm border border-border bg-card overflow-hidden">
                    <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold">Documents ({empDocs.length})</h3></div>
                    <div className="divide-y divide-border" data-testid="emp-documents-list">
                      {empDocs.length === 0 && <p className="px-6 py-8 text-center text-sm text-muted-foreground">No documents uploaded yet.</p>}
                      {empDocs.map((d) => (
                        <div key={d.id} className="flex items-center gap-3 px-6 py-3 hover:bg-muted/50 transition-colors" data-testid={`emp-doc-${d.id}`}>
                          <div className="h-9 w-9 rounded-sm bg-primary/10 text-primary flex items-center justify-center"><FileText size={16} /></div>
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{d.label || d.original_filename}</p><p className="text-xs text-muted-foreground">{d.doc_type} · {new Date(d.created_at).toLocaleDateString()}</p></div>
                          <a href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer" download={d.original_filename} className="text-primary hover:opacity-80" title="Download" data-testid={`emp-download-${d.id}`}><Download size={16} /></a>
                          <button onClick={() => deleteDoc(d.id)} className="text-muted-foreground hover:text-red-600 transition-colors" title="Delete" data-testid={`emp-delete-${d.id}`}><Trash2 size={16} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="rounded-sm" data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject {rejectFor?.kind} request</DialogTitle></DialogHeader>
          <div className="grid gap-1.5"><Label>Reason / note (emailed to employee)</Label><Textarea className="rounded-sm" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional note…" data-testid="reject-comment" /></div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700 gap-1.5" onClick={submitReject} data-testid="confirm-reject-btn"><X size={15} /> Reject & Notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
